import { PlusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { useAutomationActions } from '@mm/state/actions/automationActions';
import { IAutomationAction } from '@mm/state/model/AutomationModel';
import { WidgetTypes } from '@mm/state/model/WidgetModel';
import { getGraphLibInstance } from '@mm/state/selectors/automations';
import { selectPages } from '@mm/state/selectors/pageSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { ReactComponent as DonkeyIcon } from 'assets/icons/detector-donkey.svg';
import { ReactComponent as UnicornIcon } from 'assets/icons/detector-unicorn.svg';
import classNames from 'classnames';
import React, { useMemo } from 'react';
import { Handle, NodeProps, Position } from 'react-flow-renderer';
import { useSelector } from 'react-redux';
import css from './Adder.module.css';

type Props = {
  className?: string;
} & NodeProps<IAutomationAction>;

export const PlusButton = ({
  className,
  ...rest
}: Pick<Props, 'className'> & React.HTMLAttributes<HTMLDivElement>) => {
  return (
    <div className={classNames(className, css.container)} {...rest}>
      <span className={css.icon}>
        <PlusOutlined />
      </span>
    </div>
  );
};

export const PlusButtonSmall = ({
  className,
  ...rest
}: Pick<Props, 'className'> & React.HTMLAttributes<HTMLDivElement>) => {
  return (
    <PlusCircleOutlined
      className={classNames(className, css.small)}
      {...rest}
    />
  );
};

export const Adder = ({
  className,
  data = {
    campaignId: 0,
    dialogueId: 0,
    sortOrder: 0,
  },
  id,
}: Props) => {
  const { dialogueId, campaignId, sortOrder, pageGroupId } = data;
  const actions = useAutomationActions();

  const onClick = () => {
    if (dialogueId) {
      console.debug('Nodes: Adder: onClick', data);

      actions.showActionsList({
        dialogueId,
        campaignId,
        sortOrder,
        pageGroupId,
        widgetId: Number(id.replace('a-', '')) || undefined,
        position: 'after',
      });
    }
  };

  return (
    <>
      <Handle type="target" position={Position.Top} />
      <PlusButton className={className} onClick={onClick} />
      <Handle type="source" position={Position.Bottom} />
    </>
  );
};

export const WithAdderButton: React.FC<{
  className?: string;
  pageId?: number | null;
  sortOrder: number;
  pageGroupId?: number;
  leadType?: 'unicorn' | 'donkey';
  widgetId?: number;
}> = ({
  className,
  children,
  pageId,
  sortOrder,
  pageGroupId = 0,
  leadType,
  widgetId,
}) => {
  const actions = useAutomationActions();

  const graphLibInstance = useSelector(getGraphLibInstance);

  const widgets = useSelector((store: StoreState) => store.widget.entities);

  const pages = useSelector(selectPages);

  const hasChildren = useMemo(() => {
    return widgets.some(
      w =>
        w.pageId === pageId &&
        // ignore hidden widget tag widgets
        w.type !== WidgetTypes.TAGS,
    );
  }, [pageId, widgets]);

  const onClick = () => {
    // Note: BE is not really currently identifying
    // next page and its widget.
    // It depends us to provide next widget_id form
    // the next page with `before` parameter.
    // So we need to parse/figure it out on FE
    const successors = graphLibInstance?.successors(String(widgetId));

    if (successors?.length) {
      const nextWidgetNode = successors
        .map(nodeWidgetId => graphLibInstance?.node(nodeWidgetId))
        .find(n => n.data.widget.pageId === pageId);

      // Handle case for no next widget for a given branch
      if (!nextWidgetNode && pageId) {
        actions.showActionsList({
          dialogueId: pageId,
          sortOrder,
          pageGroupId,
          widgetId: undefined,
          position: 'after',
          isUnicornPath: leadType === 'unicorn',
        });
        return;
      }

      // Handle case for exiting next widget for a given branch
      if (nextWidgetNode.data?.widget?.pageId) {
        const pageGroup = pages.find(p => p.pageGroupId);
        actions.showActionsList({
          dialogueId: nextWidgetNode.data.widget.pageId,
          sortOrder,
          pageGroupId: pageGroup?.id,
          widgetId: nextWidgetNode.data.widget.id,
          position: 'before',
        });
      }
      return;
    }

    if (pageId) {
      console.debug('WithAdderButton: onClick', {
        pageId,
        sortOrder,
        pageGroupId,
      });

      // Handle case when both branches are empty
      actions.showActionsList({
        dialogueId: pageId,
        sortOrder,
        pageGroupId,
        widgetId,
        position: 'after',
      });
    }
  };

  return (
    <div
      className={classNames(css.wrapper, className, {
        [css.last]: !hasChildren,
      })}
    >
      {children}
      {leadType ? (
        <div
          className={
            leadType === 'unicorn' ? css.leadBannerUnicorn : css.leadBanner
          }
        >
          <div className={css.dectorIconContainer}>
            {leadType === 'unicorn' ? (
              <UnicornIcon className={css.detectorIcon} />
            ) : (
              <DonkeyIcon className={css.detectorIcon} />
            )}
          </div>
          <div>{leadType.toUpperCase()} LEADS</div>
        </div>
      ) : null}
      <div className={css.btn} onClick={onClick}>
        {hasChildren ? <PlusButtonSmall /> : <PlusButton />}
      </div>
    </div>
  );
};
